import { getUpmCategoryIds } from 'Pages/help/constants';
import { QuestionPopupModel } from 'Shared/components/design/questionnairePopup/QuestionModel';
import { QuestionnaireEventProps } from 'Shared/components/design/questionnairePopup/QuestionnaireEventModels';
import { LOCAL_STORAGE_IDS } from 'Shared/constants';
import { deleteLocalStorage, getLocalStorage, setLocalStorage } from 'Shared/services/localStorageService';
import { SurveyQualificationDetails } from 'Shared/services/localStorageTypes';
import { mapDynamicScreenerQuestionsToUnansweredQuestions } from 'Shared/services/userActivation/app/dynamicScreenerService';
import { UspSurveysModel } from 'Shared/services/usp/models/UspSurveysModel';
import { UspUnansweredQuestions } from 'Shared/services/usp/models/UspUnansweredQuestionsModel';

export interface SurveyQualificationCacheParams {
  survey?            : UspSurveysModel,
  unansweredQuestions: UspUnansweredQuestions[],
  isDynamicScreener ?: boolean,
}

export const getCachedSurveyQualificationDetails = () => getLocalStorage(LOCAL_STORAGE_IDS.SURVEY_QUAL_DETAILS, true);

export const deleteCachedSurveyQualificationDetails = () => deleteLocalStorage(LOCAL_STORAGE_IDS.SURVEY_QUAL_DETAILS);

const prepareSingleSurveyQualificationCache = (
  survey: UspSurveysModel,
  unansweredQuestions: UspUnansweredQuestions[]
): SurveyQualificationDetails => {
  const filterOutNonSurveyQuestions = unansweredQuestions.filter(question =>
    survey.unansweredQuestions.includes(question.key)
  );

  const questions: QuestionPopupModel[] = filterOutNonSurveyQuestions.map(question => ({
    key: question.key,
    multiValue: question.multiValue,
    sortOrder: question.sortOrder,
    categoryId: question.categoryId,
    data: [question]
  }));

  const questionnaireDetails: QuestionnaireEventProps = {
    points: 0,
    categoryId: getUpmCategoryIds().SURVEY_MEDLEY,
    questions: questions,
    surveyUrl: survey?.internalUrl,
    isSurveyQualification: true,
  };

  return {
    questionnaireDetails: questionnaireDetails,
    surveyPoints: survey?.points || 0
  };
};

const prepareDynamicScreenerSurveyQualificationCache = (survey: UspSurveysModel): SurveyQualificationDetails => {
  const mappedDynamicQuestions = survey.unansweredDynamicQuestionIds.length > 0
    ? mapDynamicScreenerQuestionsToUnansweredQuestions(survey)
    : [];

  const questions: QuestionPopupModel[] = mappedDynamicQuestions.map(question => ({
    key: question.key,
    multiValue: question.multiValue,
    sortOrder: question.sortOrder,
    categoryId: question.categoryId,
    data: [question]
  }));

  const questionnaireDetails: QuestionnaireEventProps = {
    points: 0,
    categoryId: getUpmCategoryIds().SURVEY_MEDLEY,
    questions: questions,
    surveyUrl: survey?.internalUrl,
    isSurveyQualification: true,
  };

  return {
    questionnaireDetails: questionnaireDetails,
    surveyPoints: survey?.points || 0
  };
};

export const prepareSurveyQualificationCache = ({
  survey,
  unansweredQuestions,
  isDynamicScreener = false
}: SurveyQualificationCacheParams): SurveyQualificationDetails => {
  if (survey && isDynamicScreener) {
    return prepareDynamicScreenerSurveyQualificationCache(survey);
  }

  if (survey) {
    return prepareSingleSurveyQualificationCache(survey, unansweredQuestions);
  }

  return {
    questionnaireDetails: {
      points: 0,
      categoryId: getUpmCategoryIds().SURVEY_MEDLEY,
      questions: [],
      isSurveyQualification: true,
    },
    surveyPoints: 0
  };
};

export const prepareAndStoreSurveyQualificationCache = (params: SurveyQualificationCacheParams): void => {
  const surveyQualificationCache = prepareSurveyQualificationCache(params);
  setLocalStorage(LOCAL_STORAGE_IDS.SURVEY_QUAL_DETAILS, surveyQualificationCache, true);
};
